// Modified by AI on 06/09/2026. Edit #3.
import React, {
    useContext, useCallback, useMemo, useEffect,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getEndOfISOWeekUnix, getStartOfISOWeekUnix } from '../utils/dateUtils';
import { QUERY_KEYS, FIVE_MINUTES_MS } from '../constants/queryKeys';
import AuthorizationContext from './AuthorizationContext';
import UserHttpService from '../api/UserHttpService';
import NotificationContext from './NotificationContext';
import SettingsContext from './SettingsContext';
import Logger from '../services/logger';

const LinkedinUserInviteLimitContext = React.createContext({
    userWeeklyInvitesLeft: null,
    countUserInvitesForWeek: () => {},
});

export const LinkedinUserInviteLimitProvider = LinkedinUserInviteLimitContext.Provider;

// this context shows how many invite requests the user can make this week
export const LinkedinUserInviteLimitApp = ({ children }) => {
    const { userLinkedinId, user } = useContext(AuthorizationContext);
    const { showErrorNotification } = useContext(NotificationContext);
    const {
        systemPreferences: {
            linkedInConnectRequestsWeeklyLimit = 100,
        },
    } = useContext(SettingsContext);

    const queryClient = useQueryClient();

    const { data: userWeeklyInvitesLeft = 0, error } = useQuery({
        queryKey: QUERY_KEYS.linkedinUserInviteLimit(userLinkedinId, linkedInConnectRequestsWeeklyLimit),
        queryFn: async () => {
            const fromDate = getStartOfISOWeekUnix(new Date());
            const toDate = getEndOfISOWeekUnix(new Date());

            const userWeeklyInvitesCount = await UserHttpService.getLinkedinInvitesCount({ userLinkedinId, fromDate, toDate });
            const userWeeklyInvitesLeftCount = linkedInConnectRequestsWeeklyLimit - userWeeklyInvitesCount > 0
                ? linkedInConnectRequestsWeeklyLimit - userWeeklyInvitesCount
                : 0;
            return userWeeklyInvitesLeftCount;
        },
        enabled: !!(user && userLinkedinId),
        staleTime: FIVE_MINUTES_MS,
        retry: false,
    });

    useEffect(() => {
        if (error) {
            Logger.error(error);
            showErrorNotification(error.message);
        }
    }, [error, showErrorNotification]);

    const countUserInvitesForWeek = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.linkedinUserInviteLimit(userLinkedinId, linkedInConnectRequestsWeeklyLimit),
            exact: true,
        });
    }, [queryClient, userLinkedinId, linkedInConnectRequestsWeeklyLimit]);

    const value = useMemo(() => ({
        userWeeklyInvitesLeft,
        countUserInvitesForWeek,
    }), [userWeeklyInvitesLeft, countUserInvitesForWeek]);

    return <LinkedinUserInviteLimitProvider
        value={value}
    >
        {children}
    </LinkedinUserInviteLimitProvider>;
};

export default LinkedinUserInviteLimitContext;
